import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

const PurchaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const server_url = localStorage.getItem("server_url");
  const grage_id = localStorage.getItem("grage_id");
  
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchPurchaseDetails();
  }, [id]);

  const fetchPurchaseDetails = async () => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append("company_id", "2");
      form.append("grage_id", grage_id);
      form.append("action", "view_purchase_details");
      form.append("purchase_id", id);
      
      const response = await axios.post(`${server_url}purchase.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const res = response.data;
      if (res.status === "1") {
        setPurchase(res.result);
      } else {
        toast.error(res.error || "Purchase not found");
        navigate("/purchases");
      }
    } catch (error) {
      console.error("Error fetching purchase details:", error);
      toast.error("Failed to load purchase details");
      navigate("/purchases");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const form = new FormData();
      form.append("company_id", "2");
      form.append("grage_id", grage_id);
      form.append("action", "delete_purchase");
      form.append("purchase_id", id);
      
      const response = await axios.post(`${server_url}purchase.php`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (response.data.status === "1") {
        toast.success("Purchase deleted successfully!");
        navigate("/purchases");
      } else {
        toast.error(response.data.error || "Failed to delete purchase");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Unable to delete purchase");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const calculateTotals = () => {
    if (!purchase?.items) return { subtotal: 0, totalGST: 0, finalTotal: 0 };
    
    let subtotal = 0;
    let totalGST = 0;
    
    purchase.items.forEach(item => {
      const basePrice = parseFloat(item.price) * parseFloat(item.quantity);
      const gstAmount = (basePrice * parseFloat(item.gst || 0)) / 100;
      subtotal += basePrice;
      totalGST += gstAmount;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      totalGST: totalGST.toFixed(2),
      finalTotal: purchase.purchase_payable_amount
    };
  };

  if (loading && !purchase) {
    return (
      <div className="mobile-app-layout" style={{ marginTop: "auto", marginBottom: "auto" }}>
        <center>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div>Loading purchase details...</div>
          </div>
        </center>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="mobile-app-layout">
        <div className="empty-state">
          <div className="empty-icon">⚠️</div>
          <div className="empty-title">Purchase Not Found</div>
          <button onClick={() => navigate("/purchases")} className="action-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="mobile-app-layout">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete purchase #{purchase.purchase_bill_number}?</p>
              <p><strong>Vendor:</strong> {purchase.vendor_name}</p>
              <p><strong>Amount:</strong> ₹{purchase.purchase_payable_amount}</p>
              <p><strong>Total GST:</strong> ₹{totals.totalGST}</p>
              <div className="delete-actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDelete} 
                  className="btn-danger"
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="content-area" style={{ paddingTop: "10px" }}>
        <div className="invoice-card">
          {/* Header */}
          <div className="invoice-header">
            <div className="invoice-meta">
              <div className="meta-row">
                <span style={{ fontSize: "18px", fontWeight: "bold", color: "#0352a1" }}>
                  Bill: #{purchase.purchase_bill_number}
                </span>
                <button
                  onClick={() => navigate(`/edit-purchase/${id}`)}
                  className="btn-primary"
                  style={{ padding: "8px 16px", fontSize: "14px" }}
                >
                  <i className="fas fa-edit"></i> Edit
                </button>
              </div>
              <div className="meta-row">
                <span>Date:</span>
                <span>{formatDate(purchase.c_date)}</span>
              </div>
            </div>
          </div>

          {/* Vendor Info */}
          <div className="info-section">
            <div className="info-column">
              <h4>Vendor Information</h4>
              <div className="info-item">
                <span>Name:</span>
                <strong>{purchase.vendor_name || "N/A"}</strong>
              </div>
              <div className="info-item">
                <span>Mobile:</span>
                <a
                  href={`tel:${purchase.vendor_mobile}`}
                  className="phone-link"
                >
                  {purchase.vendor_mobile || "N/A"}
                </a>
              </div>
              <div className="info-item">
                <span>Current Balance:</span>
                <strong style={{ color: purchase.vendor_outstanding >= 0 ? "#22543d" : "#c53030" }}>
                  ₹{purchase.vendor_outstanding || "0"}
                </strong>
              </div>
            </div>
          </div>

          {/* Purchase Items Table with GST */}
          <div className="items-section">
            <div className="section-header">
              <h4 style={{ float: "left", marginTop: "6px", fontSize: "20px" }}>
                Purchase Items
              </h4>
            </div>
            {purchase.items && purchase.items.length > 0 ? (
              <div className="items-table-container">
                <br />
                <br />
                <table
                  className="items-table"
                  style={{
                    minWidth: "-webkit-fill-available",
                    textAlign: "left",
                    marginTop: "8px",
                  }}
                >
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>GST</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchase.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_name}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.price}</td>
                        <td>{item.gst > 0 ? `${item.gst}%` : "-"}</td>
                        <td>₹{item.total_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-items">
                <p>No items found.</p>
              </div>
            )}
          </div>

          {/* Purchase Summary with GST */}
          <div className="summary-section">
            <h4>Purchase Summary</h4>
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>₹{totals.subtotal}</span>
            </div>
            <div className="summary-row">
              <span>Total GST:</span>
              <span>₹{totals.totalGST}</span>
            </div>
            <div className="summary-row">
              <span>Discount:</span>
              <span>₹{purchase.purchase_discount || "0"}</span>
            </div>
            <div className="summary-row total">
              <span>Final Total:</span>
              <strong>₹{totals.finalTotal}</strong>
            </div>
          </div>

          {/* Remarks */}
          {purchase.purchase_remark && (
            <div className="remarks-section">
              <h4>Remarks</h4>
              <p style={{ textAlign: "left" }}>
                {purchase.purchase_remark}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons" style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
            <button
              onClick={() => navigate("/purchases")}
              className="btn-secondary"
              style={{ flex: 1 }}
            >
              <i className="fas fa-arrow-left"></i> Back to List
            </button>
            <button
              onClick={confirmDelete}
              className="btn-danger"
              style={{ flex: 1 }}
            >
              <i className="fas fa-trash"></i> Delete Purchase
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .mobile-app-layout {
          min-height: 100vh;
          background: #f8fafc;
        }
        
        .content-area {
          padding: 16px;
          max-width: 800px;
          margin: 0 auto;
        }
        
        .invoice-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .invoice-header {
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 15px;
          margin-bottom: 15px;
        }
        
        .invoice-meta {
          display: grid;
          gap: 10px;
        }
        
        .meta-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #718096;
          font-size: 14px;
        }
        
        .info-section {
          background: #f0fff4;
          border: 1px solid #9ae6b4;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .info-column h4 {
          margin: 0 0 12px 0;
          color: #2d3748;
          font-size: 16px;
        }
        
        .info-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid rgba(154, 230, 180, 0.5);
        }
        
        .info-item:last-child {
          border-bottom: none;
        }
        
        .phone-link {
          color: #0352a1;
          text-decoration: none;
        }
        
        .phone-link:hover {
          text-decoration: underline;
        }
        
        .section-header {
          display: unset;
          width: 100%;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .items-section {
          margin-bottom: 20px;
        }
        
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
          margin-bottom: 16px;
        }
        
        .items-table th {
          background: #0352a1;
          color: white;
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          border: none;
          white-space: nowrap;
        }
        
        .items-table td {
          padding: 10px 8px;
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
        }
        
        .items-table td:last-child {
          border-right: none;
        }
        
        .items-table tr:nth-child(even) {
          background-color: #f8f9fa;
        }
        
        .items-table tr:hover {
          background-color: #edf2f7;
        }
        
        .no-items {
          text-align: center;
          padding: 40px 20px;
          color: #718096;
          border: 2px dashed #e2e8f0;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .summary-section {
          background: #f0fff4;
          border: 1px solid #9ae6b4;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .summary-section h4 {
          margin: 0 0 12px 0;
          color: #22543d;
        }
        
        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          color: #2d3748;
        }
        
        .summary-row.total {
          border-top: 1px solid #9ae6b4;
          padding-top: 12px;
          margin-top: 8px;
          font-size: 16px;
        }
        
        .remarks-section {
          background: #fffaf0;
          border: 1px solid #fbd38d;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .remarks-section h4 {
          margin: 0 0 8px 0;
          color: #744210;
        }
        
        .remarks-section p {
          margin: 0;
          color: #744210;
        }
        
        .btn-primary, .btn-secondary, .btn-danger {
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-size: 14px;
        }
        
        .btn-primary {
          background: #0352a1;
          color: white;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: #024685;
          transform: translateY(-1px);
        }
        
        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
        }
        
        .btn-secondary:hover:not(:disabled) {
          background: #cbd5e0;
        }
        
        .btn-danger {
          background: #dc2626;
          color: white;
        }
        
        .btn-danger:hover:not(:disabled) {
          background: #b91c1c;
        }
        
        .btn-primary:disabled, .btn-secondary:disabled, .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .loading-state, .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #718096;
        }
        
        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }
        
        .empty-title {
          font-size: 18px;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 8px;
        }
        
        .action-btn {
          background: #0352a1;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.3s ease;
        }
        
        .action-btn:hover {
          background: #3182ce;
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        
        .modal-content {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 400px;
          overflow: hidden;
        }
        
        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .modal-header h3 {
          margin: 0;
          color: #2d3748;
          font-size: 18px;
        }
        
        .modal-body {
          padding: 20px;
        }
        
        .modal-body p {
          margin: 0 0 10px 0;
          color: #4a5568;
        }
        
        .delete-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }
        
        .delete-actions button {
          flex: 1;
        }
        
        @media (max-width: 768px) {
          .content-area {
            padding: 12px;
          }
          
          .invoice-card {
            padding: 16px;
          }
          
          .items-table {
            font-size: 12px;
            min-width: 400px;
          }
          
          .action-buttons {
            flex-direction: column;
          }
        }
        
        @media (max-width: 480px) {
          .meta-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }
          
          .info-item {
            flex-direction: column;
            gap: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default PurchaseDetails;